import { useState, useEffect } from 'react'
import axios from 'axios'
import moment from 'moment'

const BoardMinutes = () => {
  const [minutes, setMinutes] = useState([])
  const [filteredMinutes, setFilteredMinutes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedYear, setSelectedYear] = useState('all')
  const [selectedMeetingType, setSelectedMeetingType] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')

  const meetingTypes = [
    { value: 'all', label: 'All Meeting Types' },
    { value: 'monthly', label: 'Monthly Board Meeting' },
    { value: 'special', label: 'Special Meeting' },
    { value: 'annual', label: 'Annual Meeting' }
  ]

  // Fetch minutes from API
  const fetchMinutes = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await axios.get('/api/minutes')

      // Sort minutes by date (most recent first)
      const sortedMinutes = response.data.sort((a, b) => {
        return new Date(b.meetingDate) - new Date(a.meetingDate)
      })

      setMinutes(sortedMinutes)
      setFilteredMinutes(sortedMinutes)
    } catch (err) {
      console.error('Error fetching minutes:', err)
      setError('Failed to load board minutes. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMinutes()
  }, [])

  // Get unique fiscal years from minutes
  const getFiscalYears = () => {
    const years = new Set()
    minutes.forEach(minute => {
      if (minute.fiscalYear) {
        // Parse fiscal year string like "2024-2025" to get the ending year
        const fiscalYearStr = minute.fiscalYear
        const endYear = parseInt(fiscalYearStr.split('-')[1])
        years.add(endYear)
      } else {
        // Calculate fiscal year from meeting date if not provided
        const date = moment(minute.meetingDate)
        const year = date.month() >= 9 ? date.year() + 1 : date.year() // October-September fiscal year
        years.add(year)
      }
    })
    return Array.from(years).sort((a, b) => b - a) // Sort descending
  }

  const fiscalYears = getFiscalYears()

  // Filter minutes by year, meeting type, and search query
  useEffect(() => {
    let filtered = minutes

    // Filter by fiscal year
    if (selectedYear !== 'all') {
      filtered = filtered.filter(minute => {
        const fiscalYear = minute.fiscalYear
          ? parseInt(minute.fiscalYear.split('-')[1])
          : calculateFiscalYear(minute.meetingDate)
        return fiscalYear === parseInt(selectedYear)
      })
    }

    // Filter by meeting type
    if (selectedMeetingType !== 'all') {
      filtered = filtered.filter(minute => minute.meetingType === selectedMeetingType)
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(minute =>
        minute.title?.toLowerCase().includes(query) ||
        minute.meetingType?.toLowerCase().includes(query) ||
        minute.topics?.some(topic => topic.toLowerCase().includes(query)) ||
        minute.summary?.toLowerCase().includes(query)
      )
    }

    setFilteredMinutes(filtered)
  }, [selectedYear, selectedMeetingType, searchQuery, minutes])

  // Calculate fiscal year from meeting date
  const calculateFiscalYear = (dateString) => {
    const date = moment(dateString)
    return date.month() >= 9 ? date.year() + 1 : date.year()
  }

  // Group minutes by fiscal year
  const groupMinutesByFiscalYear = (minutes) => {
    const grouped = {}
    minutes.forEach(minute => {
      const fiscalYear = minute.fiscalYear
        ? parseInt(minute.fiscalYear.split('-')[1])
        : calculateFiscalYear(minute.meetingDate)
      if (!grouped[fiscalYear]) {
        grouped[fiscalYear] = []
      }
      grouped[fiscalYear].push(minute)
    })
    return grouped
  }

  const groupedMinutes = groupMinutesByFiscalYear(filteredMinutes)

  // Get meeting type display name
  const getMeetingTypeLabel = (type) => {
    const meetingType = meetingTypes.find(mt => mt.value === type)
    return meetingType ? meetingType.label : type
  }

  // Get meeting type color
  const getMeetingTypeColor = (type) => {
    const colors = {
      monthly: 'blue',
      special: 'purple',
      annual: 'green'
    }
    return colors[type] || 'gray'
  }

  // Handle PDF download
  const handleDownload = async (minute) => {
    if (!minute.fileUrl) {
      alert('No file available for download')
      return
    }

    try {
      // Open PDF in new tab or trigger download
      window.open(minute.fileUrl, '_blank')
    } catch (error) {
      console.error('Error downloading file:', error)
      alert('Failed to download file. Please try again.')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="container-custom section-padding">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Board Meeting Minutes
            </h1>
            <p className="text-xl text-white/90">
              Access official records of board meetings and community decisions
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-custom py-12">
        {/* Information Banner */}
        <div className="mb-8 bg-primary-50 border-l-4 border-primary-600 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-primary-900 mb-2">
            About Board Meeting Minutes
          </h3>
          <p className="text-primary-800 mb-2">
            Board meeting minutes are official records of decisions made during board meetings.
            They include discussions on community matters, financial reports, maintenance updates,
            and policy changes.
          </p>
          <p className="text-primary-800 text-sm">
            <strong>Note:</strong> Our fiscal year runs from October 1st through September 30th.
          </p>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {/* Search Bar */}
            <div className="lg:col-span-3 xl:col-span-1">
              <label htmlFor="search" className="block text-sm font-semibold text-gray-700 mb-2">
                Search Minutes
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by title, topics, or notes..."
                  className="input pr-10"
                />
                <svg
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            {/* Fiscal Year Filter */}
            <div>
              <label htmlFor="year" className="block text-sm font-semibold text-gray-700 mb-2">
                Fiscal Year
              </label>
              <select
                id="year"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="input"
              >
                <option value="all">All Years</option>
                {fiscalYears.map(year => (
                  <option key={year} value={year}>
                    FY {year - 1}-{year}
                  </option>
                ))}
              </select>
            </div>

            {/* Meeting Type Filter */}
            <div>
              <label htmlFor="meetingType" className="block text-sm font-semibold text-gray-700 mb-2">
                Meeting Type
              </label>
              <select
                id="meetingType"
                value={selectedMeetingType}
                onChange={(e) => setSelectedMeetingType(e.target.value)}
                className="input"
              >
                {meetingTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Active Filters Display */}
          {(selectedYear !== 'all' || selectedMeetingType !== 'all' || searchQuery) && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-600">Active filters:</span>
              {selectedYear !== 'all' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  FY {selectedYear - 1}-{selectedYear}
                  <button
                    onClick={() => setSelectedYear('all')}
                    className="ml-2 hover:text-blue-900"
                  >
                    ×
                  </button>
                </span>
              )}
              {selectedMeetingType !== 'all' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {getMeetingTypeLabel(selectedMeetingType)}
                  <button
                    onClick={() => setSelectedMeetingType('all')}
                    className="ml-2 hover:text-blue-900"
                  >
                    ×
                  </button>
                </span>
              )}
              {searchQuery && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Search: "{searchQuery}"
                  <button
                    onClick={() => setSearchQuery('')}
                    className="ml-2 hover:text-blue-900"
                  >
                    ×
                  </button>
                </span>
              )}
              <button
                onClick={() => {
                  setSelectedYear('all')
                  setSelectedMeetingType('all')
                  setSearchQuery('')
                }}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Minutes Display */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-red-600 text-lg mb-4">{error}</p>
            <button onClick={fetchMinutes} className="btn btn-primary">
              Try Again
            </button>
          </div>
        ) : filteredMinutes.length === 0 ? (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Minutes Found</h3>
            <p className="text-gray-600">
              {searchQuery || selectedYear !== 'all' || selectedMeetingType !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'No board meeting minutes are currently available.'}
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedMinutes)
              .sort(([yearA], [yearB]) => yearB - yearA)
              .map(([fiscalYear, yearMinutes]) => (
                <div key={fiscalYear}>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="flex-shrink-0">
                      Fiscal Year {fiscalYear - 1}-{fiscalYear}
                    </span>
                    <span className="ml-3 flex-1 h-px bg-gray-300"></span>
                    <span className="ml-3 text-sm font-normal text-gray-500">
                      {yearMinutes.length} {yearMinutes.length === 1 ? 'meeting' : 'meetings'}
                    </span>
                  </h2>

                  <div className="space-y-4">
                    {yearMinutes.map(minute => {
                      const meetingTypeColor = getMeetingTypeColor(minute.meetingType)

                      return (
                        <div
                          key={minute._id}
                          className="card group hover:shadow-xl transition-all duration-300"
                        >
                          <div className="p-6">
                            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                              {/* Left Side - Meeting Info */}
                              <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-3 mb-3">
                                  {/* Meeting Date */}
                                  <div className="flex items-center text-gray-600">
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span className="font-semibold">
                                      {moment(minute.meetingDate).format('MMMM DD, YYYY')}
                                    </span>
                                  </div>

                                  {/* Meeting Type Badge */}
                                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-${meetingTypeColor}-100 text-${meetingTypeColor}-800`}>
                                    {getMeetingTypeLabel(minute.meetingType)}
                                  </span>
                                </div>

                                {/* Title */}
                                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                                  {minute.title}
                                </h3>

                                {/* Summary */}
                                {minute.summary && (
                                  <p className="text-sm text-gray-600 mt-2">
                                    {minute.summary}
                                  </p>
                                )}

                                {/* Topics */}
                                {minute.topics && minute.topics.length > 0 && (
                                  <div className="mb-3">
                                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                                      Topics Covered:
                                    </h4>
                                    <ul className="space-y-1">
                                      {minute.topics.map((topic, index) => (
                                        <li key={index} className="text-sm text-gray-600 flex items-start">
                                          <span className="text-primary-600 mr-2">•</span>
                                          <span>{topic}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {/* Attendees */}
                                {minute.attendees && minute.attendees.length > 0 && (
                                  <div className="mt-3 pt-3 border-t border-gray-100">
                                    <p className="text-xs text-gray-500">
                                      <span className="font-semibold">Present:</span>{' '}
                                      {minute.attendees.join(', ')}
                                    </p>
                                  </div>
                                )}
                              </div>

                              {/* Right Side - Actions */}
                              <div className="flex lg:flex-col gap-3">
                                {minute.fileUrl ? (
                                  <button
                                    onClick={() => handleDownload(minute)}
                                    className="btn btn-primary whitespace-nowrap flex items-center justify-center"
                                  >
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Download PDF
                                  </button>
                                ) : (
                                  <div className="text-sm text-gray-400 italic text-center lg:text-right">
                                    No file available
                                  </div>
                                )}

                                {/* File size if available */}
                                {minute.fileSize && (
                                  <div className="text-xs text-gray-500 text-center lg:text-right">
                                    {(minute.fileSize / 1024 / 1024).toFixed(2)} MB
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* Additional Info Section */}
        {!loading && !error && (
          <div className="mt-12 bg-secondary-50 border-l-4 border-secondary-600 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-secondary-900 mb-2">
              Questions about Board Decisions?
            </h3>
            <p className="text-secondary-800 mb-4">
              If you have questions about any decisions made during board meetings or need
              clarification on any topics discussed, please don't hesitate to reach out to
              any board member or attend the next board meeting.
            </p>
            <p className="text-sm text-secondary-700">
              Board meetings are typically held monthly. Check the Events page for upcoming
              meeting dates and times.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default BoardMinutes
